import {
  Button,
  Grid,
  Heading,
  Caption,
  Card,
  Separator,
  List,
  Drawer,
  Input,
  ActionArea,
  IOption,
  useToastAlert,
} from '@design-systems/canvas-ui'
import Icon, {
  ChevronLeftIcon,
  PencilIcon,
  PersonGroupIcon,
  InfoIcon,
  PlusCircleIcon,
} from '@design-systems/react-icons'
import { useForm, SubmitHandler, useController } from 'react-hook-form'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUserPurpose, PURPOSE_MAP } from '../../utils/teamPurposeHelpers'
import styles from './teams-create.module.css'
import { characterCount, ConfirmModal, CardIcon } from '@plm-core/utils'
import ControlledFormField from 'components/form/ControlledFormField'
import TeamMemberList from '../TeamMemberList'
import UserSearchAutocomplete, {
  UserOption,
  type UserSearchScope,
} from 'users/components/UserSearchAutocomplete'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import UserRolesCombobox from 'users/components/UserRolesCombobox'
import { CREATE_TEAM_MUTATION } from 'graphql/mutations/teams/teamMutations'
import useBusinessPartnersAutocompleteSetup from 'hooks/useBusinessPartnersAutocompleteSetup'
import ControlledAutocomplete from 'components/form/ControlledAutocomplete'
import { UserContext } from 'App/UserContext'
import { GET_BUSINESS_PARTNER_BY_ID } from 'graphql/queries/businessPartners/businessPartnerQueries'
import { PLM_SUPPORTED_ROLE_TYPES } from 'config/BusinessPartnerConsts'

type FormTeamMember = {
  id: string
  firstName: string
  lastName: string
  companyName: string
  lanId: string
  isRemoved: boolean
  roles: Array<{
    code: string
    description: string
  }>
}

type Inputs = {
  teamName: string
  isBusinessPartnerTeam: boolean
  purpose: string
  purposePlaceholder: string
  businessPartner: IOption | null
  teamPartnerType: IOption | null
  members: Array<FormTeamMember>
  newMember: UserOption | null
  newRoles: Array<any>
}

const errorToast: {
  heading: string
  message: string
  variant: 'error'
} = {
  heading: 'Unable to create team',
  message:
    'We are unable to create the team at this time. Please try again or contact support.',
  variant: 'error',
}

type PendingChange =
  | { kind: 'isBusinessPartner'; nextChecked: boolean }
  | {
      kind: 'businessPartner'
      nextValue: IOption | null
      previousValue: IOption | null
    }
  | {
      kind: 'partnerType'
      nextValue: IOption | null
      previousValue: IOption | null
    }
  | null

type PendingChangeAction =
  | { type: 'REQUEST_IS_BUSINESS_PARTNER'; nextChecked: boolean }
  | {
      type: 'REQUEST_BUSINESS_PARTNER'
      nextValue: IOption | null
      previousValue: IOption | null
    }
  | {
      type: 'REQUEST_PARTNER_TYPE'
      nextValue: IOption | null
      previousValue: IOption | null
    }
  | { type: 'CLEAR' }

const pendingChangeReducer = (
  _state: PendingChange,
  action: PendingChangeAction,
): PendingChange => {
  switch (action.type) {
    case 'REQUEST_IS_BUSINESS_PARTNER':
      return { kind: 'isBusinessPartner', nextChecked: action.nextChecked }
    case 'REQUEST_BUSINESS_PARTNER':
      return {
        kind: 'businessPartner',
        nextValue: action.nextValue,
        previousValue: action.previousValue,
      }
    case 'REQUEST_PARTNER_TYPE':
      return {
        kind: 'partnerType',
        nextValue: action.nextValue,
        previousValue: action.previousValue,
      }
    case 'CLEAR':
      return null
  }
}

const TeamsCreate = () => {
  const navigate = useNavigate()

  const makeToast = useToastAlert()
  const [userRemountKey, setUserRemountKey] = useState(0)
  const [roleRemountKey, setRoleRemountKey] = useState(0)
  const [typeRemountKey, setTypeRemountKey] = useState(0)
  const [showDrawer, setShowDrawer] = useState(false)
  const { sparkUser, isInternal } = useContext(UserContext)
  const [teamPartnerTypeOptions, setTeamPartnerTypeOptions] = useState<
    IOption[]
  >([])

  const [createTeam] = useMutation(CREATE_TEAM_MUTATION)
  const currentBPForExternal = useMemo(() => {
    return {
      id: sparkUser?.partnerId ? String(sparkUser.partnerId) : '',
      label: sparkUser?.companyName || '',
      value: sparkUser?.partnerId ? String(sparkUser.partnerId) : '',
    }
  }, [sparkUser])

  const {
    control,
    getValues,
    watch,
    setValue,
    formState: { errors, isValid, isDirty, isSubmitting },
    handleSubmit,
  } = useForm<Inputs>({
    defaultValues: {
      teamName: '',
      isBusinessPartnerTeam: !isInternal,
      purpose: '',
      purposePlaceholder: '',
      businessPartner: isInternal ? null : currentBPForExternal,
      teamPartnerType: null,
      members: [],
      newMember: null,
      newRoles: [],
    },
  })

  const { field: purposeField } = useController({
    control,
    name: 'purpose',
    rules: { required: 'Purpose is required' },
  })

  const currentPurpose = watch('purpose')
  const currentPurposePlaceholder = watch('purposePlaceholder')
  const currentTeamName = watch('teamName')
  const currentIsBusinessPartnerTeam = watch('isBusinessPartnerTeam')
  const currentMembers = watch('members')
  const currentNewMember = watch('newMember')
  const currentNewRoles = watch('newRoles')
  const currentBusinessPartner = watch('businessPartner')
  const currentPartnerType = watch('teamPartnerType')

  const purposeOptions = useMemo(() => {
    return getUserPurpose({
      isInternalTeam: !currentIsBusinessPartnerTeam,
      partnerType: currentPartnerType?.id || '',
    })
  }, [currentPartnerType, currentIsBusinessPartnerTeam])

  const shouldDisplayRoleField = useMemo(() => {
    if (!currentNewMember) return false
    return currentNewMember.data.internal === true
  }, [currentNewMember])

  const currentBusinessPartnerId = currentBusinessPartner?.id
  /*
    fetch the partner types for the selected business partner and set the options for the partner type field
    using an effect hook to watch for changes to the business partner field and fetch the partner types when it changes.
  */
  const [fetchBusinessPartnerById] = useLazyQuery(GET_BUSINESS_PARTNER_BY_ID)
  useEffect(() => {
    if (!currentBusinessPartnerId) {
      if (getValues('teamPartnerType') !== null) {
        setValue('teamPartnerType', null, { shouldDirty: false })
      }
      setTeamPartnerTypeOptions((prev) => (prev.length === 0 ? prev : []))
      setTypeRemountKey((prev) => prev + 1) // only bump if we actually cleared
      return
    }
    // stale guard to prevent setting state on unmounted component
    let ignoreResults = false
    const fetchData = async () => {
      const { data: businessPartnerData } = await fetchBusinessPartnerById({
        variables: { id: currentBusinessPartnerId },
      })
      if (ignoreResults) return
      const roles = businessPartnerData?.businessPartner?.role || []
      const options = roles
        .filter((role) => PLM_SUPPORTED_ROLE_TYPES.includes(role.id))
        .map((role) => ({
          value: String(role.id),
          label: role.name,
          id: String(role.id),
        }))
      setTeamPartnerTypeOptions(options)
      if (options.length > 0) {
        setValue(
          'teamPartnerType',
          {
            value: String(options[0].id),
            label: options[0].label,
            id: String(options[0].id),
          },
          { shouldDirty: false },
        )
      } else {
        setValue('teamPartnerType', null, { shouldDirty: false })
      }
    }
    fetchData()
    return () => {
      ignoreResults = true
    }
  }, [currentBusinessPartnerId, fetchBusinessPartnerById, getValues, setValue])

  const { handleBusinessPartnerSearchChange } =
    useBusinessPartnersAutocompleteSetup()

  const setPurposePlaceholderValue = useCallback(
    (purposePlaceholder: string) => () => {
      setValue('purposePlaceholder', purposePlaceholder, { shouldDirty: false })
    },
    [setValue],
  )

  const toggleDrawer = useCallback(() => {
    setShowDrawer((prev) => !prev)
  }, [])

  const handleTeamPurposeChange = useCallback(
    (purposeCode: string) => () => {
      purposeField.onChange(purposeCode)
      toggleDrawer()
    },
    [purposeField, toggleDrawer],
  )

  const purposeDisplay = currentPurpose
    ? (PURPOSE_MAP[currentPurpose] ?? null)
    : null

  const handleUserSearchSelect = useCallback(
    (user: UserOption) => {
      if (user) {
        setValue('newMember', user, { shouldDirty: false })
      } else {
        setValue('newMember', null, { shouldDirty: false })
      }
    },
    [setValue],
  )

  const handleRolesSelect = useCallback(
    (selectedRoles: Array<IOption>) => {
      setValue('newRoles', selectedRoles, { shouldDirty: false })
    },
    [setValue],
  )

  const handleAddMember = useCallback(() => {
    const existingMembers = getValues('members') || []
    const currentFormNewMember = getValues('newMember')
    setUserRemountKey((prev) => prev + 1)
    setRoleRemountKey((prev) => prev + 1)
    const allMembers = [...existingMembers].filter(
      (member) => member.id !== currentFormNewMember?.id,
    )
    setValue(
      'members',
      [
        ...allMembers,
        {
          id: currentFormNewMember?.id || '',
          firstName: currentFormNewMember?.data?.firstName || '',
          lastName: currentFormNewMember?.data?.lastName || '',
          companyName: currentFormNewMember?.data?.companyName || '',
          lanId: currentFormNewMember?.data?.lanId || '',
          isRemoved: false,
          roles: getValues('newRoles').map((role) => ({
            code: role.value,
            description: role.label,
          })),
        },
      ],
      { shouldDirty: true },
    )
    setValue('newMember', null, { shouldDirty: false })
    setValue('newRoles', [], { shouldDirty: false })
  }, [getValues, setValue])

  const handleRemoveMember = useCallback(
    (memberId: string) => () => {
      const updatedMembers = getValues('members').map((member) =>
        member.id === memberId ? { ...member, isRemoved: true } : member,
      )
      setValue('members', updatedMembers, { shouldDirty: true })
    },
    [getValues, setValue],
  )

  const handleUndoRemoveMember = useCallback(
    (memberId: string) => () => {
      const updatedMembers = getValues('members').map((member) =>
        member.id === memberId ? { ...member, isRemoved: false } : member,
      )
      setValue('members', updatedMembers, { shouldDirty: true })
    },
    [getValues, setValue],
  )

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data) => {
      const partnerTypeAsNumber = data.teamPartnerType?.id
        ? Number(data.teamPartnerType.id)
        : undefined
      const teamInput = {
        teamName: data.teamName,
        teamType: data.isBusinessPartnerTeam ? 'EXTERNAL' : 'INTERNAL',
        purpose: data.purpose,
        partnerId: data.isBusinessPartnerTeam
          ? String(data?.businessPartner?.id)
          : '0',
        partnerType: data.isBusinessPartnerTeam
          ? partnerTypeAsNumber
          : undefined,
        members: data.members
          .filter((member) => !member.isRemoved)
          .map((member) => ({
            userProfileId: member.id,
            roles: member.roles.map((role) => Number(role.code)),
          })),
      }

      try {
        const response = await createTeam({
          variables: { teamInput },
        })
        const newTeamId = response.data?.createTeam?.id
        if (newTeamId) {
          makeToast({
            heading: 'Team created successfully',
            message: `The team "${data.teamName}" has been created.`,
            variant: 'success',
          })
          navigate(`/teams/${newTeamId}`)
        } else {
          makeToast(errorToast)
        }
      } catch (error) {
        console.error('Error creating team:', error)
        makeToast(errorToast)
      }
    },
    [createTeam, makeToast, navigate],
  )

  const currentCompanyLabel = useMemo(() => {
    if (isInternal && !currentIsBusinessPartnerTeam) {
      return 'Target'
    }
    return currentBusinessPartner?.label || ''
  }, [isInternal, currentIsBusinessPartnerTeam, currentBusinessPartner])

  const userSearchScope = useMemo<UserSearchScope>(() => {
    if (!currentIsBusinessPartnerTeam) return { kind: 'targetOnly' }
    if (currentBusinessPartnerId) {
      return {
        kind: 'targetAndPartner',
        partnerId: Number(currentBusinessPartnerId),
      }
    }
    // BP team but BP not yet picked — restrict to Target until they choose one
    return { kind: 'targetOnly' }
  }, [currentIsBusinessPartnerTeam, currentBusinessPartnerId])

  const [pendingChange, dispatchPendingChange] = useReducer(
    pendingChangeReducer,
    null,
  )

  const handleResetUsersAndPurpose = useCallback(() => {
    setValue('newMember', null, { shouldDirty: false })
    setValue('newRoles', [], { shouldDirty: false })
    setValue('members', [], { shouldDirty: true })
    setValue('purpose', '', { shouldDirty: true })
    setValue('purposePlaceholder', '', { shouldDirty: false })
  }, [setValue])

  const handleIsBusinessPartnerChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked
      if (getValues('members').length > 0 || getValues('purpose')) {
        dispatchPendingChange({
          type: 'REQUEST_IS_BUSINESS_PARTNER',
          nextChecked: isChecked,
        })
      } else {
        setValue('isBusinessPartnerTeam', isChecked, { shouldDirty: true })
      }
    },
    [getValues, setValue],
  )

  const handleBusinessPartnerChange = useCallback(
    (option: IOption | undefined) => {
      const nextValue = option ?? null
      if (getValues('members').length > 0 || getValues('purpose')) {
        dispatchPendingChange({
          type: 'REQUEST_BUSINESS_PARTNER',
          nextValue,
          previousValue: getValues('businessPartner'),
        })
      } else {
        setValue('businessPartner', nextValue, { shouldDirty: true })
      }
    },
    [getValues, setValue],
  )

  const handlePartnerTypeChange = useCallback(
    (option: IOption | undefined) => {
      const nextValue = option ?? null
      if (getValues('purpose')) {
        dispatchPendingChange({
          type: 'REQUEST_PARTNER_TYPE',
          nextValue,
          previousValue: getValues('teamPartnerType'),
        })
      } else {
        setValue('teamPartnerType', nextValue, { shouldDirty: true })
      }
    },
    [getValues, setValue],
  )

  const handleConfirmForConfirmModal = useCallback(() => {
    if (!pendingChange) return
    switch (pendingChange.kind) {
      case 'isBusinessPartner':
        setValue('isBusinessPartnerTeam', pendingChange.nextChecked, {
          shouldDirty: true,
        })
        handleResetUsersAndPurpose()
        break
      case 'businessPartner':
        setValue('businessPartner', pendingChange.nextValue, {
          shouldDirty: true,
        })
        handleResetUsersAndPurpose()
        break
      case 'partnerType':
        setValue('teamPartnerType', pendingChange.nextValue, {
          shouldDirty: true,
        })
        setValue('purpose', '', { shouldDirty: true })
        setValue('purposePlaceholder', '', { shouldDirty: false })
        break
    }
    dispatchPendingChange({ type: 'CLEAR' })
  }, [pendingChange, setValue, handleResetUsersAndPurpose])

  const handleCancelForConfirmModal = useCallback(() => {
    if (!pendingChange) return
    switch (pendingChange.kind) {
      case 'isBusinessPartner':
        // nothing to revert: the checkbox is RHF-controlled and was never optimistically flipped
        break
      case 'businessPartner':
        setValue('businessPartner', pendingChange.previousValue, {
          shouldDirty: false,
        })
        break
      case 'partnerType':
        setValue('teamPartnerType', pendingChange.previousValue, {
          shouldDirty: false,
        })
        break
    }
    dispatchPendingChange({ type: 'CLEAR' })
  }, [pendingChange, setValue])

  return (
    <>
      <ConfirmModal
        isOpen={
          pendingChange?.kind === 'isBusinessPartner' ||
          pendingChange?.kind === 'businessPartner'
        }
        headingText="Are you sure you want to change the business partner?"
        modalSize="md"
        cancelButtonText="Cancel"
        confirmationButtonText="Confirm"
        content={
          <p>
            Changing the business partner will remove all team members and reset
            the team purpose. Are you sure you want to continue?
          </p>
        }
        onCancel={handleCancelForConfirmModal}
        onConfirm={handleConfirmForConfirmModal}
      />
      <ConfirmModal
        headingText="Are you sure?"
        modalSize="sm"
        cancelButtonText="Cancel"
        confirmationButtonText="Confirm"
        content={
          <p>
            Changing the partner type will reset the team purpose. Are you sure
            you want to continue?
          </p>
        }
        isOpen={pendingChange?.kind === 'partnerType'}
        onCancel={handleCancelForConfirmModal}
        onConfirm={handleConfirmForConfirmModal}
      />
      <Grid.Container direction="column">
        <Grid.Item size={{ xs: 12 }} className="hc8-mb-3x">
          <Grid.Container align="center">
            <Grid.Item>
              <Button
                size="lg"
                aria-label="Go back"
                isIconOnly
                as={Link}
                to="/teams"
              >
                <Icon svg={ChevronLeftIcon} />
              </Button>
            </Grid.Item>
            <Grid.Item size={{ xs: true }}>
              <Grid.Container align="center">
                <Grid.Item>
                  <Heading size={1}>Create team</Heading>
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Grid.Container
            align="center"
            justify="space-between"
            className="hc8-mb-2x"
          >
            <Grid.Item size={{ xs: true }}>
              <Caption>Add team details, members and roles.</Caption>
            </Grid.Item>
            <Grid.Item>
              <Caption>* Indicates required field</Caption>
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Card variant="elevated" className="hc8-pa-6x">
            <Grid.Container direction="column">
              <Grid.Item size={{ xs: 12 }}>
                <Grid.Container align="center">
                  <Grid.Item>
                    <CardIcon svg={InfoIcon} size="lg" />
                  </Grid.Item>
                  <Grid.Item
                    size={{
                      xs: true,
                    }}
                  >
                    <Heading size={5}>Team information</Heading>
                    <div className="hc8-clr-placeholder hc8-fs-100">
                      General details about this team.
                    </div>
                  </Grid.Item>
                  {isInternal ? (
                    <Grid.Item>
                      <ControlledFormField
                        className="hc8-pt-6x"
                        control={control}
                        type="checkbox"
                        name="isBusinessPartnerTeam"
                        label="Create a business partner team"
                        onChange={handleIsBusinessPartnerChange}
                      />
                    </Grid.Item>
                  ) : null}
                </Grid.Container>
              </Grid.Item>
              <Grid.Item size={{ xs: 12 }}>
                <Grid.Container align="center">
                  <Grid.Item size={{ xs: true }}>
                    <ControlledFormField
                      control={control}
                      name="teamName"
                      rules={{
                        required: 'Team name is required',
                        maxLength: { value: 75, message: 'Max length is 75' },
                      }}
                      id="teamName"
                      label="Team name"
                      type="text"
                      isRequired
                      hintText={characterCount(currentTeamName, 75)}
                      isErrored={!!errors.teamName}
                      errorText={errors.teamName?.message}
                    />
                  </Grid.Item>
                  <Grid.Item
                    className={`hc8-pb-6x ${isInternal && currentIsBusinessPartnerTeam ? '' : 'hc8-pl-2x'}`}
                    size={{ xs: true }}
                  >
                    {isInternal && currentIsBusinessPartnerTeam ? (
                      <ControlledAutocomplete
                        control={control}
                        name="businessPartner"
                        label="Company"
                        options={handleBusinessPartnerSearchChange}
                        onChange={handleBusinessPartnerChange}
                      />
                    ) : (
                      <Grid.Container direction="column">
                        <Grid.Item>
                          <strong>Company</strong>
                        </Grid.Item>
                        <Grid.Item>{currentCompanyLabel}</Grid.Item>
                      </Grid.Container>
                    )}
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
              {currentIsBusinessPartnerTeam ? (
                <Grid.Item size={{ xs: 12 }}>
                  <Grid.Container justify="end">
                    <Grid.Item size={{ xs: 6 }}>
                      <ControlledAutocomplete
                        key={typeRemountKey}
                        control={control}
                        rules={{
                          required: 'Partner type is required',
                        }}
                        name="teamPartnerType"
                        label="Partner type"
                        options={teamPartnerTypeOptions}
                        isRequired
                        openOnFocus
                        onChange={handlePartnerTypeChange}
                        isDisabled={teamPartnerTypeOptions.length === 0}
                        helperText={
                          teamPartnerTypeOptions.length === 0
                            ? 'No approved partner types available for this business partner'
                            : undefined
                        }
                      />
                    </Grid.Item>
                  </Grid.Container>
                </Grid.Item>
              ) : null}
              <Grid.Item size={{ xs: 12 }}>
                <Drawer
                  headingText="Choose this team's purpose"
                  isOpen={showDrawer}
                  onRequestClose={toggleDrawer}
                  footerContent={
                    <Button
                      className="hc8-wth-100"
                      variant="primary"
                      isDisabled={!currentPurposePlaceholder}
                      onClick={handleTeamPurposeChange(
                        currentPurposePlaceholder,
                      )}
                    >
                      Save changes
                    </Button>
                  }
                >
                  <List>
                    {purposeOptions.map((purpose) => {
                      return (
                        <List.Item
                          key={purpose.code}
                          className={`${styles.canvasBorder} hc8-mb-2x`}
                          onClick={setPurposePlaceholderValue(purpose.code)}
                        >
                          <Grid.Container align="center" size={{ xs: 12 }}>
                            <Grid.Item size={{ xs: 1 }}>
                              <Input.Radio
                                name={`teamPurpose-${purpose.code}`}
                                value={purpose.code}
                                isChecked={
                                  purpose.code === currentPurposePlaceholder
                                }
                              />
                            </Grid.Item>
                            <Grid.Item size={{ xs: 11 }}>
                              <Caption
                                className="hc8-ml-8x"
                                below={purpose.description}
                              >
                                <strong>{purpose.displayName}</strong>
                              </Caption>
                            </Grid.Item>
                          </Grid.Container>
                        </List.Item>
                      )
                    })}
                  </List>
                </Drawer>
                <ActionArea onClick={toggleDrawer}>
                  <Grid.Container
                    className={`${styles.canvasBorder} hc8-pa-4x`}
                    align="center"
                  >
                    <Grid.Item size={{ xs: true }}>
                      <Caption
                        below={
                          purposeDisplay?.description ||
                          "This helps tailor PLM to your team's needs by showing only the sections that matter for their work."
                        }
                      >
                        {purposeDisplay?.displayName ||
                          "Choose this team's purpose *"}
                      </Caption>
                    </Grid.Item>
                    <Grid.Item>
                      <Icon svg={PencilIcon} />
                    </Grid.Item>
                  </Grid.Container>
                </ActionArea>
              </Grid.Item>
            </Grid.Container>
          </Card>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Card variant="elevated">
            <Grid.Container direction="column" className="hc8-pa-6x">
              <Grid.Item size={{ xs: 12 }}>
                <Grid.Container align="center">
                  <Grid.Item>
                    <CardIcon svg={PersonGroupIcon} size="lg" />
                  </Grid.Item>
                  <Grid.Item
                    size={{
                      xs: true,
                    }}
                  >
                    <Heading size={5}>Team members</Heading>
                    <div className="hc8-clr-placeholder hc8-fs-100">
                      Manage access and roles for this team.
                    </div>
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
              <Grid.Item size={{ xs: 12 }}>
                <Grid.Container align="end">
                  <Grid.Item size={{ xs: 4 }}>
                    <UserSearchAutocomplete
                      key={userRemountKey}
                      label="Add a team member"
                      onUserSelect={handleUserSearchSelect}
                      scope={userSearchScope}
                      value={currentNewMember}
                    />
                  </Grid.Item>
                  {shouldDisplayRoleField && (
                    <Grid.Item size={{ xs: 4 }}>
                      <UserRolesCombobox
                        key={roleRemountKey}
                        className="hc8-mb-1x"
                        label={`Roles ${currentNewRoles?.length > 0 ? `(${currentNewRoles.length})` : ''}`}
                        placeholder="Search by role"
                        onRolesSelect={handleRolesSelect}
                        value={currentNewRoles}
                      />
                    </Grid.Item>
                  )}
                  <Grid.Item size={{ xs: true }}>
                    <Button
                      className="hc8-mb-1x"
                      variant="default"
                      isDisabled={currentNewMember === null}
                      onClick={handleAddMember}
                    >
                      <Icon svg={PlusCircleIcon} />
                      Add to team
                    </Button>
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
              <Grid.Item>
                <Separator />
              </Grid.Item>
              {currentMembers.length > 0 && (
                <Grid.Item
                  size={{ xs: 12 }}
                  className={styles.userListContainer}
                >
                  <TeamMemberList
                    teamMembers={currentMembers}
                    onUndoRemoveMember={handleUndoRemoveMember}
                    onRemoveMember={handleRemoveMember}
                  />
                </Grid.Item>
              )}
              {currentMembers.length === 0 && (
                <Grid.Item size={{ xs: 12 }}>
                  <Grid.Container
                    align="center"
                    justify="center"
                    className={`${styles.usersPlaceHolder}`}
                  >
                    <Grid.Item>
                      <Caption below="Use the search above to find a user and add them to the team.">
                        <strong>No team members added yet.</strong>
                      </Caption>
                    </Grid.Item>
                  </Grid.Container>
                </Grid.Item>
              )}
            </Grid.Container>
          </Card>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Grid.Container justify="end">
            <Grid.Item>
              <Button as={Link} to="/teams">
                Cancel
              </Button>
            </Grid.Item>
            <Grid.Item>
              <Button
                isDisabled={!isValid || !isDirty || isSubmitting}
                variant="primary"
                onClick={handleSubmit(onSubmit)}
              >
                Create team
              </Button>
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
      </Grid.Container>
    </>
  )
}

export default TeamsCreate
